import { Text } from '@thanx/uikit/text'
import { useGetOrderSatisfactionQuery } from 'api/orderSatisfaction'
import { ReactComponent as ThumbsUpIcon } from 'assets/images/feedbacks/ic-thumbs-up.svg'
import ContentContainer from 'components/ContentContainer'
import LiveUpdatesDisabledBanner from 'components/LiveUpdatesDisabledBanner'
import { LIVE_UPDATES_PATH } from 'constants/liveUpdates'
import { useCanOpenLiveUpdates } from 'hooks/useCanOpenLiveUpdates'
import useDispatch from 'hooks/useDispatch'
import { useLiveUpdatesState } from 'hooks/useLiveUpdatesState'
import { buildTranslate } from 'locales'
import React from 'react'
import { push } from 'react-router-redux'
import { getBenchmarkRate } from 'scenes/Reports/helpers'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { percentageFormatter } from 'utilities/formatters'
import Benchmark from '../Benchmark'
import Card from '../Card'
import { MetricCardT } from './index'

const t = buildTranslate('home.metrics')
const liveUpdatesT = buildTranslate('live_updates')

// Zero decimals per the design (`96%` / `85%`) — not reportPercentage's fixed 1.
const RATE_DECIMALS = 0
// No responded ratings → the `-%` token the sibling metric cards already use
// for a nil rate (scenes/Reports/helpers.ts `reportPercentage`), never a
// fabricated `0%`.
const NO_DATA_PLACEHOLDER = '-%'
const THUMB_ICON_SIZE_PX = 38

const OrderSatisfactionMetric = ({ merchant }: MetricCardT) => {
  const dispatch = useDispatch()
  const [css] = useStyletron()
  const liveUpdatesState = useLiveUpdatesState()
  const canOpenLiveUpdates = useCanOpenLiveUpdates()
  // No feature flag gate: the report (and this card, which the parent
  // <Metrics/> already gates on report permission) is universal post-GA, so the
  // query runs for every merchant that can see the card.
  const { data, isLoading, isError } = useGetOrderSatisfactionQuery(undefined)
  // This is a satisfaction-score metric card, so it wants the Order ratings
  // REPORT (Looker aggregate), not the Order feedback Overview/Feedback/
  // Settings page — the two live at different routes.

  const { rate: benchmarkRate } = getBenchmarkRate(
    merchant.merchant_type,
    'order_satisfaction'
  )

  const satisfactionRate = data?.satisfactionRate ?? null
  const hasRate = satisfactionRate !== null
  const value =
    satisfactionRate === null
      ? NO_DATA_PLACEHOLDER
      : percentageFormatter(satisfactionRate, RATE_DECIMALS, RATE_DECIMALS)

  return (
    <Card
      isFullWidth
      title={t('order_satisfaction.title')}
      onClick={() => dispatch(push('/order_ratings'))}
      sections={[
        { content: t('order_satisfaction.popovers.section_1') },
        {
          title: t('shared.popovers.how_its_calculated'),
          content: t('order_satisfaction.popovers.section_2'),
        },
      ]}
    >
      <ContentContainer isLoading={isLoading} isErrored={isError}>
        <Text.BodyText1 tag="p" className="m-0" bold>
          {hasRate ? (
            <>
              {value}
              <ThumbsUpIcon
                data-testid="order-satisfaction-thumb"
                className={`ml-xs ${css({
                  width: `${THUMB_ICON_SIZE_PX}px`,
                  height: `${THUMB_ICON_SIZE_PX}px`,
                  color: merchantTheme.colors.nephritis50,
                  verticalAlign: 'middle',
                })}`}
                aria-hidden="true"
              />
            </>
          ) : (
            <>
              {/* `-%` is decorative — a screen reader would announce a bare dash,
                  so hide it and expose real text instead. */}
              <span aria-hidden="true">{value}</span>
              <span className="sr-only">{t('order_satisfaction.no_data')}</span>
            </>
          )}
        </Text.BodyText1>
      </ContentContainer>
      {liveUpdatesState.isDisabled && (
        <div className="mt-s" data-testid="order-satisfaction-disabled-banner">
          <LiveUpdatesDisabledBanner
            body={liveUpdatesT('disabled_banner.home_card.body')}
            resumeText={liveUpdatesT('disabled_banner.home_card.resume')}
            resume={
              canOpenLiveUpdates
                ? { kind: 'link', to: LIVE_UPDATES_PATH }
                : undefined
            }
            compact
            headingTag="h5"
          />
        </div>
      )}
      <Benchmark
        benchmarkRate={benchmarkRate}
        labelKey="industry_average"
        decimals={RATE_DECIMALS}
      />
    </Card>
  )
}

export default OrderSatisfactionMetric
